/**
 * GoSportWizard - Conteneur principal du wizard Go Sport
 * Gère la navigation entre les 6 étapes + welcome
 */

import React, { useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Dumbbell, ChevronLeft } from 'lucide-react';

import { StepWelcome } from './steps/StepWelcome';
import { StepEvaluation } from './steps/StepEvaluation';
import { StepHealth } from './steps/StepHealth';
import { StepPreSession } from './steps/StepPreSession';
import { StepSessionSetup } from './steps/StepSessionSetup';
import { StepSessionLive } from './steps/StepSessionLive';
import { StepPostSession } from './steps/StepPostSession';
import { StepHistory } from './steps/StepHistory';

const STEPS = [
  { id: 'welcome', label: 'Accueil', canGoBack: false },
  { id: 'evaluation', label: 'Évaluation', canGoBack: true },
  { id: 'health', label: 'Santé', canGoBack: true },
  { id: 'pre-session', label: 'Pré-séance', canGoBack: true },
  { id: 'session-setup', label: 'Séance', canGoBack: true },
  { id: 'session-live', label: 'LIVE', canGoBack: false },
  { id: 'post-session', label: 'Récup', canGoBack: false },
  { id: 'history', label: 'Historique', canGoBack: true },
];

export function GoSportWizard({
  goSport,
  onClose,
}) {
  const {
    data, hasProfile, wizardStep, currentSession, sessionExercises, stats,
    updateProfile, saveEvaluationAnswers, generateNewSession, startSession,
    completeSession, abandonSession, replaceExercise, setCurrentSession,
    nextStep, prevStep, goToStep, closeWizard,
    interruptedSession, resumeInterruptedSession, discardInterruptedSession,
  } = goSport;

  const [levelChange, setLevelChange] = React.useState(null); // { leveledUp, leveledDown, newLevel }

  const step = STEPS[wizardStep] || STEPS[0];
  const isLive = step.id === 'session-live';

  const handleClose = useCallback(() => {
    if (isLive) return;
    closeWizard();
    onClose?.();
  }, [isLive, closeWizard, onClose]);

  const handleEvaluationComplete = useCallback((answers) => {
    const level = saveEvaluationAnswers(answers);
    nextStep();
    return level;
  }, [saveEvaluationAnswers, nextStep]);

  const handleHealthSkip = useCallback(() => {
    updateProfile({ disclaimerAccepted: true });
    nextStep();
  }, [updateProfile, nextStep]);

  const handlePreSessionSkip = useCallback(() => {
    nextStep();
  }, [nextStep]);

  const handleGenerateSession = useCallback((overrides) => {
    generateNewSession(overrides);
  }, [generateNewSession]);

  const handleStartSession = useCallback(() => {
    startSession();
    nextStep();
  }, [startSession, nextStep]);

  const handleSessionComplete = useCallback((timerData) => {
    const completed = completeSession({
      completedCycles: timerData.completedCycles,
    });
    setCurrentSession(completed);
    if (completed.leveledUp || completed.leveledDown) {
      setLevelChange({
        leveledUp: completed.leveledUp,
        leveledDown: completed.leveledDown,
        newLevel: completed.newLevel,
      });
    }
    nextStep();
  }, [completeSession, setCurrentSession, nextStep]);

  const handleAbandon = useCallback(() => {
    abandonSession();
    goToStep(0);
  }, [abandonSession, goToStep]);

  const handleRate = useCallback((rating) => {
    if (currentSession) {
      setCurrentSession(prev => prev ? { ...prev, rating } : prev);
    }
  }, [currentSession, setCurrentSession]);

  const handlePostClose = useCallback(() => {
    closeWizard();
    onClose?.();
  }, [closeWizard, onClose]);

  const handleQuickStart = useCallback(() => {
    generateNewSession();
    goToStep(4);
  }, [generateNewSession, goToStep]);

  const handleStartEvaluation = useCallback(() => {
    goToStep(1);
  }, [goToStep]);

  const handleShowHistory = useCallback(() => {
    goToStep(7);
  }, [goToStep]);

  const handleHistoryStartSession = useCallback(() => {
    generateNewSession();
    goToStep(4);
  }, [generateNewSession, goToStep]);

  const handleHistoryClose = useCallback(() => {
    goToStep(0);
  }, [goToStep]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[320] flex flex-col bg-zinc-900"
    >
      {/* Header */}
      {!isLive && (
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800">
          <div className="flex items-center gap-3">
            {step.canGoBack && wizardStep > 0 && (
              <button onClick={prevStep} className="p-1 text-zinc-400 hover:text-white">
                <ChevronLeft className="w-5 h-5" />
              </button>
            )}
            <div className="flex items-center gap-2">
              <Dumbbell className="w-5 h-5 text-orange-400" />
              <span className="font-bold text-white">Go Sport</span>
            </div>
          </div>

          <button
            onClick={handleClose}
            className="p-2 text-zinc-400 hover:text-white rounded-lg hover:bg-zinc-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Progress bar wizard */}
      {!isLive && wizardStep > 0 && wizardStep < 6 && wizardStep !== 7 && (
        <div className="px-4 py-2 flex gap-1">
          {STEPS.slice(1, 6).map((s, i) => (
            <div
              key={s.id}
              className={`h-1 flex-1 rounded-full transition-all duration-300
                ${i < wizardStep ? 'bg-orange-500' : i === wizardStep - 1 ? 'bg-orange-400' : 'bg-zinc-700'}`}
            />
          ))}
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <AnimatePresence mode="wait">
          {wizardStep === 0 && (
            <StepWelcome
              key="welcome"
              hasProfile={hasProfile}
              stats={stats}
              interruptedSession={interruptedSession}
              onStart={handleStartEvaluation}
              onQuickStart={handleQuickStart}
              onShowHistory={handleShowHistory}
              onResumeSession={resumeInterruptedSession}
              onDiscardSession={discardInterruptedSession}
            />
          )}

          {wizardStep === 1 && (
            <StepEvaluation
              key="evaluation"
              onComplete={handleEvaluationComplete}
              initialAnswers={data.profile.evaluationAnswers || {}}
            />
          )}

          {wizardStep === 2 && (
            <StepHealth
              key="health"
              profile={data.profile}
              onUpdate={updateProfile}
              onNext={nextStep}
              onSkip={handleHealthSkip}
            />
          )}

          {wizardStep === 3 && (
            <StepPreSession
              key="pre-session"
              level={data.userLevel || 1}
              profile={data.profile}
              onNext={nextStep}
              onSkip={handlePreSessionSkip}
            />
          )}

          {wizardStep === 4 && (
            <StepSessionSetup
              key="session-setup"
              level={data.userLevel || 1}
              profile={data.profile}
              exercises={sessionExercises}
              onGenerate={handleGenerateSession}
              onStart={handleStartSession}
              onReplaceExercise={replaceExercise}
            />
          )}

          {wizardStep === 5 && (
            <StepSessionLive
              key="session-live"
              session={currentSession}
              exercises={sessionExercises}
              onComplete={handleSessionComplete}
              onAbandon={handleAbandon}
            />
          )}

          {wizardStep === 6 && (
            <StepPostSession
              key="post-session"
              session={currentSession}
              level={data.userLevel || 1}
              stats={stats}
              onRate={handleRate}
              onClose={handlePostClose}
              levelChange={levelChange}
            />
          )}

          {wizardStep === 7 && (
            <StepHistory
              key="history"
              stats={stats}
              sessions={data.sessions || []}
              level={data.userLevel || 1}
              streak={data.streak}
              profile={data.profile}
              onClose={handleHistoryClose}
              onStartSession={handleHistoryStartSession}
            />
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}
